import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Check, Loader2, Swords, RotateCcw, Shuffle, X, ArrowLeft } from 'lucide-react';

import { useChallengeCreation } from './useChallengeCreation';
import { ScorecardPreview } from './ScorecardPreview';
import { SocialShareButtons } from './SocialShareButtons';

/**
 * Modal for creating and sharing a puzzle challenge.
 * One-click sharing: scorecard appears automatically via OG meta tags.
 */
export function CreateChallengeModal({
  isOpen,
  onClose,
  imageUrl,
  difficulty,
  playerName: initialPlayerName,
  scoreData,
  time,
  moves,
  deviceId,
  onPlayChallenge,
  pushNotifications,
  onReplayPuzzle,
  onNewPuzzle
}) {
  const {
    step,
    challengeCode,
    scorecardReady,
    scorecardKey,
    scorecardError,
    handleScorecardError
  } = useChallengeCreation({
    isOpen,
    onClose,
    initialPlayerName,
    imageUrl,
    difficulty,
    scoreData,
    time,
    moves,
    deviceId,
    pushNotifications
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-auto box-border" data-testid="challenge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Swords className="h-5 w-5 text-primary" />
            Challenge Friends
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 w-full max-w-full">
          {step === 'creating' && (
            <div className="py-8 text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <div>
                <p className="font-medium">Creating Your Challenge...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Saving your score and generating share card
                </p>
              </div>
            </div>
          )}

          {step === 'share' && challengeCode && (
            <div className="space-y-3 sm:space-y-4">
              <div className="text-center py-2 sm:py-3">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-success/10 mb-2">
                  <Check className="h-5 w-5 sm:h-6 sm:w-6 text-success" />
                </div>
                <p className="font-medium text-sm sm:text-base">Challenge Created!</p>
                <p className="text-xl sm:text-2xl font-heading font-bold text-primary mt-1">
                  {challengeCode}
                </p>
              </div>

              <ScorecardPreview
                challengeCode={challengeCode}
                scorecardKey={scorecardKey}
                isReady={scorecardReady}
                hasError={scorecardError}
                onError={handleScorecardError}
              />

              <SocialShareButtons challengeCode={challengeCode} />

              <div className="flex gap-2 sm:gap-3">
                <Button 
                  className="flex-1 h-10 sm:h-12 text-sm sm:text-base px-2 sm:px-4 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                  onClick={() => { onClose(); onNewPuzzle?.(); }}
                  data-testid="challenge-new-puzzle-btn"
                >
                  <Shuffle className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">New Puzzle</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
